import React, { useState } from 'react';
import { joinWaitlist } from '../../agents/publicAgent';
import { LEGAL_DOCUMENTS } from '../Legal/legalDocuments';
import { WAITLIST_STATES } from '../../utils/publicSite';
import styles from './WaitlistForm.module.css';

const INTENTS = [
  { value: 'buy', label: 'Buying' },
  { value: 'sell', label: 'Selling' },
  { value: 'both', label: 'Both' },
];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const stateName = (code) => WAITLIST_STATES.find(([value]) => value === code)?.[1] || code;

/**
 * Waitlist sign-up for states cutthe6.com doesn't serve yet
 * (POST /api/public/waitlist). The server answers the same way for a new or
 * repeat sign-up, so the form never reveals whether an email is already on it.
 */
export default function WaitlistForm() {
  const [email, setEmail] = useState('');
  const [state, setState] = useState('');
  const [intent, setIntent] = useState('both');
  const [status, setStatus] = useState('idle'); // idle | submitting | done
  const [error, setError] = useState('');
  const [joinedState, setJoinedState] = useState('');

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!EMAIL_PATTERN.test(email.trim())) {
      setError('Enter a valid email address.');
      return;
    }
    if (!state) {
      setError('Choose the state you want us in.');
      return;
    }
    setError('');
    setStatus('submitting');
    try {
      await joinWaitlist({ email: email.trim(), state, intent });
      setJoinedState(state);
      setStatus('done');
    } catch (err) {
      setStatus('idle');
      if (err?.status === 429) {
        setError('Too many tries from this connection. Please wait a while and try again.');
      } else if (err?.status === 422) {
        setError('Check your email address and state, then try again.');
      } else {
        setError("We couldn't add you just now. Please try again.");
      }
    }
  };

  if (status === 'done') {
    return (
      <div className={styles.card} role="status" data-testid="waitlist-success">
        <p className={styles.doneTitle}>You&apos;re on the list for {stateName(joinedState)}.</p>
        <p className={styles.doneBody}>We&apos;ll email you if cutthe6.com opens there.</p>
        <button
          type="button"
          className={styles.again}
          onClick={() => { setStatus('idle'); setState(''); }}
        >
          Add another state
        </button>
      </div>
    );
  }

  return (
    <form className={styles.card} onSubmit={handleSubmit} noValidate data-testid="waitlist-form">
      {error && (
        <p className={styles.error} role="alert" id="waitlist-error">{error}</p>
      )}
      <div className={styles.field}>
        <label htmlFor="waitlist-email" className={styles.label}>Email</label>
        <input
          id="waitlist-email"
          type="email"
          className={styles.input}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          placeholder="you@example.com"
          aria-describedby={error ? 'waitlist-error' : undefined}
          required
        />
      </div>
      <div className={styles.field}>
        <label htmlFor="waitlist-state" className={styles.label}>State</label>
        <select
          id="waitlist-state"
          className={styles.input}
          value={state}
          onChange={(e) => setState(e.target.value)}
          required
        >
          <option value="">Choose a state</option>
          {WAITLIST_STATES.map(([code, name]) => (
            <option key={code} value={code}>{name}</option>
          ))}
        </select>
      </div>
      <fieldset className={styles.intents}>
        <legend className={styles.label}>I&apos;m interested in</legend>
        <div className={styles.intentRow}>
          {INTENTS.map((option) => (
            <label key={option.value} className={styles.intent}>
              <input
                type="radio"
                name="waitlist-intent"
                value={option.value}
                checked={intent === option.value}
                onChange={() => setIntent(option.value)}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
      </fieldset>
      <button type="submit" className={styles.submit} disabled={status === 'submitting'}>
        {status === 'submitting' ? 'Adding you…' : 'Join the waitlist'}
      </button>
      <p className={styles.privacy}>
        We&apos;ll only use your email to tell you when we launch in your state. Unsubscribe anytime.
        See our <a href={LEGAL_DOCUMENTS.privacy.path}>{LEGAL_DOCUMENTS.privacy.title}</a>.
      </p>
    </form>
  );
}
